<template>
  <div class="confirmation-container d-flex align-items-center justify-content-center">
    <div class="confirmation-card" ref="cardRef">
      <form @submit.prevent="handleSubmit" class="confirmation-form">

        <!-- Encabezado Minimalista Fino -->
        <div class="form-header anim-el">
          <h1 class="rsvp-title">R S V P</h1>
          <p class="form-subtitle">Confirma tu asistencia<br><strong>Confirma tu asistencia antes del 28 de Noviembre</strong></p>
        </div>

        <!-- Nombre del Invitado (Campo Editable con Línea Fina Inferior) -->
        <div class="input-line-group anim-el mb-4">
          <input
            id="fullname"
            type="text"
            v-model.trim="guestName"
            class="line-input"
            placeholder="Escribe tu Nombre Completo"
            required
          />
        </div>

        <!-- Asistencia (Opciones limpias sin contornos redondos) -->
        <div class="form-group anim-el mb-4">
          <label class="form-label mb-3">¿Asistirás?</label>
          <div class="radio-group column">
            <label class="radio-option-clean" :class="{ selected: attending === 'si' }">
              <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
              <span class="radio-label">Sí, ¡No me la pierdo!</span>
            </label>

            <label class="radio-option-clean" :class="{ selected: attending === 'no' }">
              <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
              <span class="radio-label">No podré asistir, ¡Lo siento!</span>
            </label>
          </div>
        </div>

        <!-- ¿Cuántos asisten? (Opciones del 1 al 5) -->
        <div class="form-group anim-el mb-4" v-if="attending !== 'no'">
          <label for="guests" class="form-label mb-2">¿Cuántos asisten?</label>
          <div class="select-line-wrap">
            <select
              id="guests"
              v-model="guests"
              class="line-select"
              :required="attending === 'si'"
            >
              <option value="" disabled>Selecciona número de personas</option>
              <option v-for="n in [1, 2, 3, 4, 5]" :key="n" :value="n">
                {{ n }} {{ n === 1 ? 'Persona' : 'Personas' }}
              </option>
            </select>
          </div>
        </div>

        <!-- Botón Rectangular Delgado Minimalista -->
        <div class="actions anim-el mt-4">
          <button
            type="submit"
            class="outline-btn"
            :disabled="isSubmitting"
          >
            <span v-if="!isSubmitting">CONFIRMAR</span>
            <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </button>
        </div>

        <div v-if="errorMessage" class="alert-error anim-el text-center mt-3">{{ errorMessage }}</div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import axios from 'axios'
import baseUrl from '../api'
import Swal from 'sweetalert2'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'ConfirmacionAsistenciaUltraLimpia',
  props: {
    idInvitado:  { type: Number, required: false, default: null },
    nombre:      { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup (props) {
    const guestName     = ref('')
    const attending     = ref('')
    const guests        = ref('')
    const isSubmitting  = ref(false)
    const errorMessage  = ref('')

    const cardRef = ref(null)

    const animateOnView = () => {
      const targets = cardRef.value?.querySelectorAll('.anim-el')
      if (!targets || !targets.length) return

      // Animación con GSAP ScrollTrigger
      gsap.fromTo(
        targets,
        {
          opacity: 0,
          y: 25
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: cardRef.value,
            start: 'top 85%',
            toggleActions: 'play none none none',
            once: true
          }
        }
      )
    }

    const handleSubmit = async () => {
      if (!guestName.value) {
        Swal.fire({
          icon: 'warning',
          title: 'Atención',
          text: 'Por favor escribe tu nombre completo.',
          confirmButtonColor: '#1a1a1a',
          allowOutsideClick: true
        })
        return
      }

      if (!attending.value) {
        Swal.fire({
          icon: 'warning',
          title: 'Atención',
          text: 'Por favor selecciona si asistirás o no.',
          confirmButtonColor: '#1a1a1a',
          allowOutsideClick: true
        })
        return
      }

      if (attending.value === 'si' && !guests.value) {
        await Swal.fire({
          icon: 'warning',
          title: 'Atención',
          text: 'Selecciona el número de asistentes.',
          confirmButtonColor: '#1a1a1a',
          allowOutsideClick: true
        })
        await nextTick()
        const sel = document.getElementById('guests')
        if (sel) sel.focus()
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        const numPasesToSend = attending.value === 'si' ? Number(guests.value) : 0

        const formData = {
          nombre_asistencia: guestName.value,
          telefono_invitado: '',
          invitedby: 'novia',
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado || null,
          num_pases: numPasesToSend
        }

        await axios.post(`${baseUrl}/asistencia`, formData, {
          headers: { 'Content-Type': 'application/json' }
        })

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: '¡Gracias por avisarnos!',
            text: 'Sentimos mucho que no puedas asistir.',
            confirmButtonColor: '#1a1a1a',
          })
        } else {
          Swal.fire({
            icon: 'success',
            title: '¡Gracias!',
            text: 'Tu asistencia quedó confirmada.',
            confirmButtonColor: '#1a1a1a',
          })
        }

        guestName.value = ''
        attending.value = ''
        guests.value = ''
      } catch (error) {
        console.error('Error confirmando asistencia:', error)
        errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.'
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(async () => {
      await nextTick()
      animateOnView()
    })

    return {
      guestName,
      attending,
      guests,
      isSubmitting,
      errorMessage,
      handleSubmit,
      cardRef
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400&display=swap');

/* ===== CONTENEDOR Y TARJETA ===== */
.confirmation-container {
  width: 100%;
  padding: 35px 15px;
  display: flex;
  margin-bottom: 0px;
  align-items: center;
  justify-content: center;
  background-color: rgba(217, 212, 209, 0.95);
  box-sizing: border-box;
}

.confirmation-card {
  width: 100%;
  max-width: 330px;
  background-color: rgba(217, 212, 209, 0.95);
  text-align: center;
  padding: 10px 5px;
}

/* ===== TÍTULO EDITORIAL ===== */
.rsvp-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.6rem;
  letter-spacing: 0.38em;
  color: #1a1a1a;
  font-weight: 500;
  margin-bottom: 2px;
  text-indent: 0.38em;
}

.form-subtitle {
  font-family: 'Playfair Display', serif;
  font-size: 0.85rem;
  font-style: italic;
  color: #4a4a4a;
  margin-bottom: 22px;
}

/* ===== INPUTS LÍNEA INFERIOR ===== */
.input-line-group { 
  width: 100%; 
}

.line-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid #1a1a1a;
  background: transparent;
  padding: 6px 0;
  font-family: 'Playfair Display', serif;
  font-size: 0.95rem;
  color: #1a1a1a;
  text-align: center;
  outline: none;
  box-sizing: border-box;
}

.line-input::placeholder {
  color: #8c8c8c;
  font-style: italic;
}

/* ===== ETIQUETAS Y GRUPOS ===== */
.form-group { 
  margin: 14px 0; 
}

.form-label {
  font-family: 'Playfair Display', serif;
  font-size: 0.88rem;
  display: block;
  color: #2b2b2b;
  text-align: center;
}

/* ===== RADIOS LIMPIOS SIN RECUADROS NI BORDES REDONDOS ===== */
.radio-group.column { 
  display: flex; 
  flex-direction: column; 
  gap: 10px; 
  align-items: center;
}

.radio-option-clean {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 4px 0;
  background: transparent;
  transition: opacity 0.2s ease;
}

.radio-option-clean:hover {
  opacity: 0.8;
}

.radio-input {
  accent-color: #1a1a1a;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.radio-label {
  font-family: 'Playfair Display', serif;
  font-size: 0.85rem;
  color: #2b2b2b;
}

.radio-option-clean.selected .radio-label {
  font-weight: 600;
  color: #1a1a1a;
}

/* ===== SELECT CON LÍNEA INFERIOR (ULTRA LIMPIO) ===== */
.select-line-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
}

.line-select {
  width: 100%;
  border: none;
  border-bottom: 1px solid #1a1a1a;
  border-radius: 0;
  background: transparent;
  padding: 6px 0;
  font-family: 'Playfair Display', serif;
  font-size: 0.88rem;
  color: #1a1a1a;
  outline: none;
  text-align: center;
  text-align-last: center;
  cursor: pointer;
}

.line-select option {
  font-family: 'Playfair Display', serif;
  font-size: 0.88rem;
  color: #1a1a1a;
  background: #ffffff;
}

/* ===== BOTÓN RECTANGULAR DELGADO ===== */
.actions {
  display: flex;
  justify-content: center;
}

.outline-btn {
  width: 100%;
  padding: 10px;
  border: 1px solid #1a1a1a;
  border-radius: 0;
  background: transparent;
  font-family: 'Playfair Display', serif;
  font-size: 0.82rem;
  color: #1a1a1a;
  letter-spacing: 0.18em;
  cursor: pointer;
  transition: all 0.25s ease;
}

.outline-btn:hover {
  background: #1a1a1a;
  color: #ffffff;
}

.outline-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.alert-error {
  font-family: 'Playfair Display', serif;
  font-size: 0.78rem;
  color: #a84242;
  margin-top: 8px;
}
</style>